import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from './api';
import { useAuth } from './AuthContext';
import useVisibilityAwareInterval from './Hooks/useVisibilityAwareInterval';
import { API_BASE_URL } from './config';

const SettingsContext = createContext(null);

function findCapability(capabilities, keys) {
  return capabilities.find((item) => keys.includes(item.key));
}

export const SettingsProvider = ({ children }) => {
  const { logout } = useAuth();
  const [chatEnabled, setChatEnabled] = useState(false);
  const [imageAnalysisEnabled, setImageAnalysisEnabled] = useState(false);
  const [assistantCapabilities, setAssistantCapabilities] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectDelayRef = useRef(1000);
  const settingsInFlightRef = useRef(false);

  // Poll the settings while the tab is visible. Pause when hidden.
  const fetchSettings = useCallback(async () => {
    // Collapse overlapping triggers (interval + visibility resume) into one request.
    if (settingsInFlightRef.current) return;
    settingsInFlightRef.current = true;
    try {
      // Polled settings refresh - background, so a blip cannot sign the user out.
      const respCapabilities = await apiFetch('/settings/assistant-capabilities', {}, { onUnauthorized: logout, background: true });
      if (respCapabilities && respCapabilities.ok) {
        const data = await respCapabilities.json();
        const capabilities = Array.isArray(data?.capabilities) ? data.capabilities : [];
        setAssistantCapabilities(capabilities);
        const staffChat = findCapability(capabilities, ['staff_chat_enabled', 'chat_enabled']);
        const imageEvaluation = capabilities.find((item) => item.key === 'image_analysis_enabled');
        if (typeof staffChat?.enabled === 'boolean') setChatEnabled(staffChat.enabled);
        if (typeof imageEvaluation?.enabled === 'boolean') setImageAnalysisEnabled(imageEvaluation.enabled);
      } else {
        const respChat = await apiFetch('/settings/chat', {}, { onUnauthorized: logout, background: true });
        if (respChat && respChat.ok) {
          const data = await respChat.json();
          if (typeof data.enabled === 'boolean') setChatEnabled(data.enabled);
        }

        const respImg = await apiFetch('/settings/image-analysis', {}, { onUnauthorized: logout, background: true });
        if (respImg && respImg.ok) {
          const data = await respImg.json();
          if (typeof data.enabled === 'boolean') setImageAnalysisEnabled(data.enabled);
        }
      }
    } catch (e) {
      // ignore and leave current value
    } finally {
      settingsInFlightRef.current = false;
    }
  }, [logout]);

  // run every 60s while visible; run immediately on mount/when visibility resumes
  useVisibilityAwareInterval(fetchSettings, 60_000, { immediate: true });

  const connectWebSocket = useCallback(() => {
    const baseUrl = API_BASE_URL;
    if (!baseUrl) return;

    try {
      const url = new URL('/settings/ws', baseUrl);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';

      if (wsRef.current) {
        wsRef.current.close();
      }

      const socket = new WebSocket(url.toString());
      wsRef.current = socket;

      socket.onopen = () => {
        reconnectDelayRef.current = 1000;
        try {
          socket.send(JSON.stringify({ type: 'ping' }));
        } catch (e) {
          // ignore
        }
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data && (data.key === 'staff_chat_enabled' || data.key === 'chat_enabled')) {
            setChatEnabled(Boolean(data.enabled));
          }
          if (data && data.key === 'image_analysis_enabled') {
            setImageAnalysisEnabled(Boolean(data.enabled));
          }
          if (data && typeof data.key === 'string' && typeof data.enabled === 'boolean') {
            setAssistantCapabilities((current) => current.map((item) => (
              item.key === data.key ? { ...item, enabled: data.enabled } : item
            )));
          }
        } catch (e) {
          // swallow malformed payloads
        }
      };

      socket.onclose = () => {
        if (wsRef.current === socket) {
          wsRef.current = null;
          if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
          }
          const delay = reconnectDelayRef.current;
          reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, 30000);
          reconnectTimerRef.current = window.setTimeout(connectWebSocket, delay);
        }
      };

      socket.onerror = () => {
        socket.close();
      };
    } catch (e) {
      // if URL parsing fails, skip connection
    }
  }, [setChatEnabled, setImageAnalysisEnabled]);

  useEffect(() => {
    connectWebSocket();
    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connectWebSocket]);

  const value = {
    chatEnabled,
    setChatEnabled,
    imageAnalysisEnabled,
    setImageAnalysisEnabled,
    assistantCapabilities,
    setAssistantCapabilities,
    fetchSettings
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
